import { useLocalStorage } from "@rehooks/local-storage";
import { useCallback, useEffect } from "react";
import { MemoryRouter } from "react-router";

import { Cross } from "components/icons/generated";
import Box from "ds/components/Box";
import ButtonIcon from "ds/components/ButtonIcon";
import Link from "ds/components/Link";
import Typography from "ds/components/Typography";

import { FLOATING_BAR_ID, SHOW_DEVTOOLS_KEY } from "./constants";
import { useModifiedFlags } from "./flags/useFlags";
import { showDevToolsModal } from "./Modal";
import useStrictModeWithExpiry from "./performance/strictMode/useStrictModeWithExpiry";
import styles from "./styles.module.css";
import { HIGHLIGHTS_ENABLED } from "./xRay/constants";
import { useXRay } from "./xRay/useXRay";

const prId = process.env.REACT_APP_PR_ID;

const getClearLinkPath = () => {
  const clearLinkQueryString = new URLSearchParams(window.location.search);
  clearLinkQueryString.set("pr", "null");

  return `${window.location.pathname}?${clearLinkQueryString}`;
};

const FloatingBar = () => {
  const [flags, , clearFlags] = useModifiedFlags();
  const [highlightsEnabled] = useLocalStorage(HIGHLIGHTS_ENABLED);
  const [showDevTools, setShowDevTools] = useLocalStorage(SHOW_DEVTOOLS_KEY, false);
  const {
    isEnabled: isStrictModeEnabled,
    setEnabled: setStrictModeEnabled,
    cleanupExpiredStrictMode,
  } = useStrictModeWithExpiry();

  useXRay();

  const hasModifiedFlags = Object.keys(flags).length > 0;

  const showModal = useCallback((e: KeyboardEvent) => {
    if (e.ctrlKey && e.shiftKey && e.key === "Y") {
      showDevToolsModal();
    }
  }, []);

  useEffect(() => {
    window.showDevTools = () => {
      setShowDevTools(true);
    };

    window.hideDevTools = () => {
      setShowDevTools(false);
      clearFlags();
    };

    cleanupExpiredStrictMode();

    return () => {
      delete window.showDevTools;
      delete window.hideDevTools;
    };
  }, [setShowDevTools, clearFlags, cleanupExpiredStrictMode]);

  useEffect(() => {
    if (!showDevTools) {
      return;
    }

    window.addEventListener("keydown", showModal);

    return () => {
      window.removeEventListener("keydown", showModal);
    };
  }, [showModal, showDevTools]);

  if (!showDevTools && !prId) {
    return null;
  }

  return (
    <MemoryRouter useTransitions={false}>
      <Box className={styles.bar} align="center">
        {!!prId && (
          <>
            <Box className={styles.section} align="center" padding="0 medium">
              <Typography variant="p-body3" tag="span">
                Preview of PR{" "}
                <Link
                  href={`https://github.com/spacelift-io/frontend/pull/${prId}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  size="small"
                  variant="secondary"
                >
                  #{prId}
                </Link>
              </Typography>
            </Box>
            <Box className={styles.section} padding="0 medium">
              <Link size="small" variant="secondary" href={getClearLinkPath()}>
                Clear preview
              </Link>
            </Box>
          </>
        )}

        {isStrictModeEnabled && (
          <Box className={styles.section} padding="0 medium">
            <Link
              size="small"
              variant="secondary"
              onPress={() => {
                setStrictModeEnabled(false);
              }}
            >
              <Typography variant="p-body3" tag="span">
                Clear StrictMode
              </Typography>
            </Link>
          </Box>
        )}

        {showDevTools && (
          <Box className={styles.section} padding="0 medium">
            <Link
              size="small"
              variant="secondary"
              onPress={() => {
                showDevToolsModal();
              }}
            >
              DevTools
              {highlightsEnabled && <strong>X-ray</strong>}
              {hasModifiedFlags && ` (flags modified)`}
            </Link>
          </Box>
        )}

        <Box className={styles.section}>
          <ButtonIcon
            icon={Cross}
            onPress={() => {
              document.getElementById(FLOATING_BAR_ID)?.remove();
            }}
            variant="ghost"
            excludeFromTabOrder={true}
          >
            Dismiss
          </ButtonIcon>
        </Box>
      </Box>
    </MemoryRouter>
  );
};

export default FloatingBar;
